import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import MedicalRecordsModal from './MedicalRecordsModal';
import Sidebar from './Sidebar';
import AppointmentCalendar from './calendar/AppointmentCalendar';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [vets, setVets] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedVet, setSelectedVet] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [isMedicalRecordsModalOpen, setIsMedicalRecordsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (clinic) {
      fetchVets();
      fetchCustomers();
    }
  }, [clinic]); // Only run when clinic changes

  useEffect(() => {
    if (clinic && vets.length > 0) {
      fetchAppointments();
    }
  }, [clinic, selectedDate, selectedVet, vets.length]); // Run when these change, but only if vets are loaded

  const checkUser = async () => {
    try {


      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        if (import.meta.env.DEV) console.error('❌ Error getting user:', userError);
        navigate('/login');
        return;
      }
      
      if (!user) {
        if (import.meta.env.DEV) console.log('❌ No user found, redirecting to login');
        navigate('/login');
        return;
      }

      setUser(user);

      // Fetch clinic data (supports both owners and vet employees)
      const { clinic: clinicData, error: clinicResolveError } = await resolveClinicForUser(supabase, user.id);

      if (clinicResolveError || !clinicData) {
        if (import.meta.env.DEV) console.error('Error resolving clinic:', clinicResolveError);
        showToast('Δεν βρέθηκε κλινική για αυτόν τον χρήστη. Παρακαλώ επικοινωνήστε με τον διαχειριστή.', 'error');
        return;
      }
      setClinic(clinicData);
    } catch (error) {
      if (import.meta.env.DEV) console.error('❌ Unexpected error in checkUser:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Removed createClinic function

  // Toast notification functions
  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const fetchVets = async () => {
    try {
      const { data, error } = await supabase
        .from('vets')
        .select('*')
        .eq('clinic_id', clinic.id);

      if (error) throw error;

      setVets(data);

      // Set default selected vet based on number of vets
      if (data.length === 1) {
        setSelectedVet(data[0].id);
      } else if (data.length > 1) {
        setSelectedVet('all');
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching vets:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const { count, error } = await supabase
        .from('owners')
        .select('id', { count: 'exact', head: true })
        .eq('clinic_id', clinic.id)
        .is('deleted_at', null);

      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching customers:', error);
      } else {
        setCustomers({ length: count || 0 });
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching customers:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      // Calculate date range for calendar view (current week +/- 2 weeks for better navigation)
      const startDate = new Date(selectedDate);
      startDate.setDate(startDate.getDate() - 14);
      const endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + 14);

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      // Use optimized database function for better performance
      const { data, error } = await supabase.rpc('get_clinic_appointments', {
        p_clinic_id: clinic.id,
        p_start_date: startStr,
        p_end_date: endStr,
        p_vet_id: selectedVet === 'all' ? null : selectedVet,
        p_limit: 500,
        p_offset: 0
      });

      if (error) throw error;

      // Transform data to match expected format
      const formattedAppointments = (data || []).map(apt => ({
        id: apt.id,
        pet_id: apt.pet_id,
        appointment_date: apt.appointment_date,
        duration_minutes: apt.duration_minutes,
        notes: apt.notes,
        status: apt.status,
        vet_id: apt.vet_id,
        pets: { name: apt.client_pet_name || apt.pet_name || null, species: apt.pet_species },
        owners: {
          first_name: apt.client_first_name || apt.owner_name?.split(' ')[0] || '',
          last_name: apt.client_last_name || apt.owner_name?.split(' ').slice(1).join(' ') || ''
        },
        vets: {
          first_name: apt.vet_name?.split(' ')[0] || '',
          last_name: apt.vet_name?.split(' ').slice(1).join(' ') || ''
        },
        client_phone: apt.client_phone || null
      }));

      setAppointments(formattedAppointments);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };

  // Update appointment status helper (with rate limiting)
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      // Check rate limit (max 50 status updates per minute)
      const { data: allowed, error: rateLimitError } = await supabase.rpc('check_rate_limit', {
        p_user_id: user.id,
        p_operation: 'update_appointment_status',
        p_limit: 50,
        p_window_minutes: 1
      });

      if (rateLimitError) {
        if (import.meta.env.DEV) console.warn('Rate limit check failed:', rateLimitError);
        // Continue anyway if rate limit check fails (graceful degradation)
      } else if (!allowed) {
        showToast('Πάρα πολλές ενέργειες! Παρακαλώ περιμένετε λίγο.', 'error');
        return;
      }

      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);

      if (error) throw error;

      // Log the activity
      try {
        await supabase.from('activity_log').insert({
          actor_id: user.id,
          action: 'update_appointment_status',
          table_name: 'appointments',
          record_id: appointmentId,
          new_values: { status: newStatus }
        });
      } catch (logErr) {
        if (import.meta.env.DEV) console.warn('Activity log failed:', logErr);
      }

      // Refresh list to reflect status change
      await fetchAppointments();
      const statusMessages = {
        completed: 'Το ραντεβού ολοκληρώθηκε.',
        cancelled: 'Το ραντεβού ακυρώθηκε.',
        no_show: 'Το ραντεβού σημειώθηκε ως μη εμφάνιση.'
      };
      showToast(statusMessages[newStatus] || 'Η κατάσταση ενημερώθηκε.', 'success');
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to update appointment status:', err);
      showToast('Αποτυχία ενημέρωσης ραντεβού.', 'error');
    }
  };

  // Complete appointment (for calendar action modal)
  const completeAppointment = useCallback(async (appointmentId) => {
    await updateAppointmentStatus(appointmentId, 'completed');
  }, [updateAppointmentStatus]);

  // Cancel appointment (for calendar action modal)
  const cancelAppointment = useCallback(async (appointmentId) => {
    await updateAppointmentStatus(appointmentId, 'cancelled');
  }, [updateAppointmentStatus]);

  // No-show appointment (for calendar action modal)
  const noShowAppointment = useCallback(async (appointmentId) => {
    await updateAppointmentStatus(appointmentId, 'no_show');
  }, [updateAppointmentStatus]);

  // Handle calendar view history - navigate to pet history
  const handleViewHistory = useCallback((petId) => {
    if (petId) {
      navigate(`/pet-medical-history/${petId}`);
    }
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // Map appointment status to badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10B981';
      case 'completed': return '#3B82F6';
      case 'in_progress': return '#F59E0B';
      case 'cancelled': return '#EF4444';
      case 'scheduled': return '#6B7280';
      case 'no_show': return '#9CA3AF';
      default: return '#4B5563';
    }
  };


  // Compute today's appointments count (always actual today, not selected date)
  const todayStr = new Date().toISOString().split('T')[0];
  const todaysAppointments = appointments.filter(apt => {
    const aptDate = apt.appointment_date?.split('T')[0];
    return aptDate === todayStr && apt.status !== 'cancelled';
  });

  // Today's working vets (unique vets with appointments today)
  const todaysWorkingVets = new Set(todaysAppointments.map(apt => apt.vet_id)).size;

  // Today's clients (unique owners with appointments today)
  const todaysClients = new Set(todaysAppointments.map(apt => `${apt.owners?.first_name} ${apt.owners?.last_name}`)).size;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A202C' }}>
        <div className="text-center">
          {/* Animated spinner with gradient */}
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full animate-spin"
            style={{
              background: 'conic-gradient(from 0deg, transparent 0%, #10B981 50%, #06B6D4 100%)',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #fff calc(100% - 4px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #fff calc(100% - 4px))'
            }}
          />
          <p className="text-gray-400 text-sm animate-pulse">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Πίνακας Ελέγχου</h1>
              <p className="text-gray-400 text-sm lg:text-base">
                Καλώς ήρθατε, {user?.email ? `Δρ. ${user.email.split('@')[0]}` : 'Δρ.'}
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-emerald-400 font-medium text-sm">
                {new Date().toLocaleDateString('el-GR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 lg:mb-8">
          {/* Today's Appointments Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-emerald-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: 'rgba(16,185,129,0.2)', color: '#10B981' }}>Σήμερα</span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Σημερινά Ραντεβού</p>
            <span className="text-3xl font-bold text-white">{todaysAppointments.length}</span>
          </div>

          {/* Vets Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-cyan-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(6,182,212,0.15)' }}>
                <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Σημερινοί Κτηνίατροι</p>
            <span className="text-3xl font-bold text-white">{todaysWorkingVets}</span>
          </div>

          {/* Customers Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-violet-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(139,92,246,0.15)' }}>
                <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Σημερινοί Πελάτες</p>
            <span className="text-3xl font-bold text-white">{todaysClients}</span>
          </div>
        </div>

        {/* Show clinic creation prompt if no clinic exists */}
        {!clinic ? (
          <div
            className="rounded-xl p-8 border text-center"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}
            >
              <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Δεν βρέθηκε κλινική</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Για να χρησιμοποιήσετε τη λειτουργικότητα του πίνακα ελέγχου, πρέπει πρώτα να δημιουργήσετε την κλινική σας.
            </p>
          </div>
        ) : (
          <>
        {/* Vet Filter */}
        <div
          className="mb-6 lg:mb-8 rounded-xl p-5 border"
          style={{
            background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
            borderColor: '#374151',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h3 className="text-base lg:text-lg font-semibold text-white">Φιλτράρισμα ανά Κτηνίατρο</h3>
          </div>

          {/* Vet Cards */}
          <div className="flex items-stretch gap-3 overflow-x-auto pb-2 -mb-2">
            {/* "All" option - shown when more than 1 vet */}
            {vets.length > 1 && (
              <div
                onClick={() => setSelectedVet('all')}
                className={`flex-shrink-0 cursor-pointer transition-all duration-200 rounded-xl p-4 min-w-[100px] flex flex-col items-center justify-center border-2 ${
                  selectedVet === 'all'
                    ? 'border-emerald-500 hover:border-emerald-400'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
                style={{
                  background: selectedVet === 'all'
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(6,182,212,0.1) 100%)'
                    : 'rgba(17, 24, 39, 0.5)',
                  boxShadow: selectedVet === 'all'
                    ? '0 4px 15px rgba(16,185,129,0.2)'
                    : 'none',
                  transform: selectedVet === 'all' ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                <div
                  className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center mb-2"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)'
                  }}
                >
                  <svg className="w-6 h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className={`text-xs lg:text-sm font-medium text-center ${
                  selectedVet === 'all' ? 'text-emerald-400' : 'text-gray-300'
                }`}>
                  Όλοι
                </p>
              </div>
            )}

            {/* Individual vet cards */}
            {vets.map((vet) => (
              <div
                key={vet.id}
                onClick={() => setSelectedVet(vet.id)}
                className={`flex-shrink-0 cursor-pointer transition-all duration-200 rounded-xl p-4 min-w-[100px] flex flex-col items-center justify-center border-2 ${
                  selectedVet === vet.id
                    ? 'border-emerald-500 hover:border-emerald-400'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
                style={{
                  background: selectedVet === vet.id
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(6,182,212,0.1) 100%)'
                    : 'rgba(17, 24, 39, 0.5)',
                  boxShadow: selectedVet === vet.id
                    ? '0 4px 15px rgba(16,185,129,0.2)'
                    : 'none',
                  transform: selectedVet === vet.id ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                <div
                  className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center text-lg lg:text-xl font-bold text-white mb-2"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)'
                  }}
                >
                  {vet.first_name?.[0]}{vet.last_name?.[0]}
                </div>
                <p className={`text-xs lg:text-sm font-medium text-center max-w-[80px] truncate ${
                  selectedVet === vet.id ? 'text-emerald-400' : 'text-gray-300'
                }`}>
                  {vet.first_name}
                </p>
                <p className={`text-xs text-center max-w-[80px] truncate ${
                  selectedVet === vet.id ? 'text-emerald-400/70' : 'text-gray-500'
                }`}>
                  {vet.last_name}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Appointments Calendar */}
        <AppointmentCalendar
          appointments={appointments.filter(apt => apt.status !== 'cancelled')}
          vets={vets}
          selectedVet={selectedVet}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onComplete={completeAppointment}
          onCancel={cancelAppointment}
          onNoShow={noShowAppointment}
          onViewHistory={handleViewHistory}
          showToast={showToast}
          getStatusColor={getStatusColor}
        />

        {/* Scroll to top button - mobile only */}
        <div className="lg:hidden flex justify-center mt-6 mb-2">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)',
              border: '1px solid rgba(16,185,129,0.3)',
              color: '#10B981'
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            Πάνω
          </button>
        </div>

        {/* Medical Records Modal */}
        <MedicalRecordsModal
          isOpen={isMedicalRecordsModalOpen}
          onClose={() => setIsMedicalRecordsModalOpen(false)}
          clinicId={clinic?.id}
        />
        </>
        )}
      </div>

      {/* Mobile overlay to close sidebar when clicking outside */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* Removed Clinic Creation Modal */}
      
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ease-in-out ${
          toast.show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}>
          <div
            className="px-5 py-4 rounded-xl border max-w-sm"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#06B6D4',
              boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(6,182,212,0.2)'
              }`
            }}
          >
            <div className="flex items-center gap-3">
              {toast.type === 'success' && (
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {toast.type === 'error' && (
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              {toast.type === 'info' && (
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(6,182,212,0.15)' }}>
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
              <span className={`font-medium text-sm ${
                toast.type === 'success' ? 'text-emerald-400' :
                toast.type === 'error' ? 'text-red-400' : 'text-cyan-400'
              }`}>{toast.message}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 